/** Inbox (/w/:ws/inbox/:conversationId?) — engage group. */
export const inbox = {
  title: "Inbox",
  live: "Live",
  connecting: "Connecting…",
  reconnecting: "Reconnecting…",
  liveHint: "New conversations and replies appear here automatically.",
  views: {
    label: "Views",
    all: "All",
    needs_staff: "Needs staff",
    mine: "Assigned to me",
    my_teams: "My teams",
    ai_resolved: "AI-resolved",
    leads: "Leads",
    snoozed: "Snoozed",
    closed: "Closed",
  },
  snooze: {
    until: "Snoozed until {{when}}",
    heading: "Snoozed",
    wakes: "Comes back at {{when}}, or as soon as the customer or a teammate replies.",
    unsnooze: "Unsnooze",
    woken: "Conversation unsnoozed",
    emptyTitle: "Nothing is snoozed",
    emptyBody: "A workflow can snooze a conversation until a set time. It waits here, and comes back when the time is up or when the customer or a teammate replies.",
  },
  search: "Search name, message, tag…",
  filters: {
    open: "Filter",
    openActive_one: "Filter ({{count}} active)",
    openActive_other: "Filter ({{count}} active)",
    heading: "Filter conversations",
    channel: "Channel",
    agent: "Agent",
    team: "Team",
    anyTeam: "Any team",
    tag: "Tag",
    anyTag: "Any tag",
    date: "Date",
    priority: "Priority",
    anyPriority: "Any priority",
    sort: "Sort by",
    sorts: { recent: "Newest first", priority: "Priority first" },
    ranges: { any: "Any time", today: "Today", "7d": "Last 7 days", "30d": "Last 30 days" },
    reset: "Reset",
    apply: "Apply",
  },
  priority: {
    label: "Priority",
    normal: "Normal",
    high: "High",
    urgent: "Urgent",
    changed: "Priority set to {{priority}}",
  },
  language: {
    other: "Other language: {{name}}",
    hint: "Written in {{name}}. The assistant does not speak it: it replies in English unless a language was chosen for this conversation.",
    names: { ceb: "Cebuano", ilo: "Ilocano", hil: "Hiligaynon", zh: "Chinese" },
  },
  sla: {
    dueIn: "Due in {{time}}",
    breached: "Breached",
    stages: { first_response: "First response", next_response: "Next response", resolution: "Resolution" },
  },
  rating: {
    title: "Customer rating",
    outOf: "{{rating}} out of {{max}}",
    up: "Thumbs up",
    down: "Thumbs down",
    ratedAt: "Rated {{time}}",
  },
  list: {
    label: "Conversations",
    shown: "{{shown}} of {{total}} in this view",
    shownOpen: "{{shown}} shown",
    loadOlder: "Load older",
    unread: "Unread",
    verified: "Verified",
    ai: "AI",
    staff: "Staff · {{name}}",
    team: "Team",
    emptyTitle: "Nothing in this view",
    emptyBody: "Try another view or clear your search.",
    clearSearch: "Clear search & filters",
    loadError: "Conversations couldn't load.",
  },
  status: {
    label: "Status",
    needsStaff: "Needs staff",
    open: "Open",
    waiting: "Waiting",
    resolved: "Resolved",
    closed: "Closed",
    abandoned: "Abandoned",
    lead: "Lead",
    changed: "Conversation marked {{status}}",
  },
  thread: {
    back: "Back to list",
    started: "Started {{time}}",
    assign: "Assign",
    assignTo: "Assign to",
    assignedTo: "Assigned to {{name}}",
    assignedToast: "Assigned to {{name}}",
    you: "You",
    unassign: "Unassign",
    teamRoundRobin: "{{team}} (round-robin)",
    takeOver: "Take over from AI",
    returnToAi: "Hand back to AI",
    tookOver: "You took over — AI paused for this conversation",
    returned: "Handed back to AI — the assistant will answer new messages",
    toggleDetails: "Toggle details",
    selectTitle: "Pick a conversation",
    selectBody: "Choose a conversation from the list to read the thread and reply.",
    loadError: "This conversation couldn't load.",
    today: "Today · {{date}}",
    messagesLabel: "Messages",
    aiMeta: "AI",
    seconds: "{{value}}s",
    sources_one: "{{count}} source",
    sources_other: "{{count}} sources",
    confidence: "confidence {{value}}",
    staffMeta: "{{name}} · Staff",
    delivered: "Delivered",
    queued: "Sending…",
    outsideWindow: "Not delivered — outside the 24-hour reply window",
    noteHeading: "Internal note — only staff can see · {{name}} · {{time}}",
    labelledNoteHeading: "{{label}} — only staff can see · {{name}} · {{time}}",
    staffMember: "Staff member",
    citationFallback: "Source {{n}}",
    location: "p.{{page}}",
  },
  events: {
    handoff_requested: "Handoff requested · {{time}}",
    handoffReason: "Handoff requested: {{reason}} · {{time}}",
    escalated: "Escalated to the Owner — no one picked this up in time · {{time}}",
    assigned: "Assigned to {{name}} · {{time}}",
    unassigned: "Unassigned · {{time}}",
    status_changed: "Status changed to {{status}} · {{time}}",
    takeover: "{{name}} took over — AI paused · {{time}}",
    released: "Handed back to AI · {{time}}",
    returned_to_queue: "Returned to the team queue · {{time}}",
    contact_captured: "Visitor left an email address · {{time}}",
    added_to_knowledge: "Saved to the knowledge base · {{time}}",
    rated: "Visitor rated this conversation · {{time}}",
    snoozed: "Workflow “{{workflow}}” snoozed this conversation until {{when}} · {{time}}",
    snoozedPlain: "Snoozed until {{when}} · {{time}}",
    snoozedNoTime: "Conversation snoozed · {{time}}",
    unsnoozed: {
      timer: "Snooze ended: time was up · {{time}}",
      visitor_reply: "Snooze ended: the customer replied · {{time}}",
      staff_reply: "Snooze ended: a teammate replied · {{time}}",
      closed: "Snooze ended: the conversation was closed · {{time}}",
    },
    unsnoozedBy: "Unsnoozed by {{name}} · {{time}}",
    someone: "A teammate",
    reasons: {
      asked_for_person: "user asked for a person",
      knowledge_gap: "no answer in the knowledge base",
      low_confidence: "low confidence answer",
      payment: "payment question",
      negative_sentiment: "visitor seems frustrated",
      keyword: "escalation keyword",
      other: "other",
    },
  },
  handoff: {
    title: "Handoff",
    titleReason: "Handoff: {{reason}}",
    waiting: "Waiting for staff",
    outOfHours: "Office closed",
    routedTo: "Routed to",
    teamQueue: "{{team}} team queue",
    ownersAndAdmins: "Owner and Admins",
    visitorEmail: "Visitor email",
    aiSummary: "AI summary",
    autoSummary: "Summary from the visitor's words",
  },
  composer: {
    tabs: "Composer mode",
    reply: "Reply",
    note: "Internal note",
    replyingOn: "Replying on {{channel}} as",
    staffOnly: "Visible to staff only",
    locked: "AI is handling this chat. Take over to reply as staff, or leave an internal note.",
    replyPlaceholder: "Type a reply…  (Enter to send, Shift+Enter for a new line)",
    notePlaceholder: "Write an internal note — only staff can see this. Use @ to mention a teammate.",
    label: "Message",
    savedReplies: "Saved replies",
    noSavedReplies: "No saved replies yet",
    draftWithAi: "Draft with AI",
    drafting: "Drafting…",
    draftReady: "Draft ready — review before sending",
    draftFailed: "The AI couldn't draft a reply right now.",
    send: "Send",
    addNote: "Add note",
    sent: "Reply sent",
    noteAdded: "Internal note added",
    empty: "Write something first",
    sendFailed: "Your reply wasn't sent. Please try again.",
    enterToSend: "send",
    readOnly: "You can read this conversation but not reply.",
    messengerPause: "Replying on Messenger pauses the assistant for 12 hours.",
  },
  details: {
    title: "Details",
    close: "Close details",
    identity: "Identity",
    channel: "Channel",
    location: "Location",
    device: "Device",
    firstSeen: "First seen",
    language: "Language",
    page: "Page",
    verified: "Verified",
    notVerified: "not verified",
    maskedId: "ID",
    identityTypes: {
      anonymous: "Anonymous · cookie ID",
      lead_otp: "Verified by one-time code",
      messenger: "Messenger profile",
      sso: "Single sign-on",
      jwt: "Signed-in user",
      email: "Email address",
    },
    lead: "Lead captured",
    openLead: "Open lead",
    mobile: "Mobile",
    email: "Email",
    interest: "Interest",
    stage: "Stage",
    tags: "Tags",
    addTag: "Add tag…",
    removeTag: "Remove tag {{tag}}",
    tagAdded: "Tag added",
    tagRemoved: "Tag removed",
    messages: "Messages",
    confidence: "AI conf. avg",
    sla: "Handoff SLA",
    slaWaiting: "Waiting {{time}}",
    slaReplied: "Replied in {{time}}",
    slaNone: "No handoff",
    slaOverdue: "Over {{minutes}} min",
    slaEscalated: "Owner and Admins notified",
    sourcesUsed: "Sources used",
    noSources: "No sources cited yet",
    previous: "Previous conversations",
    noPrevious: "First conversation with this visitor",
    handledByAi: "AI",
    handledByStaff: "Staff",
    untitled: "Conversation",
    actionsTaken: "Actions taken",
    noActions: "No actions ran in this conversation",
    actionStatus: "{{status}} · {{ms}} ms",
    addToKnowledge: "Add to Knowledge",
    exportTranscript: "Export transcript",
    exported: "Transcript exported · {{file}}",
  },
  knowledge: {
    title: "Add to Knowledge",
    description: "Save this answer as a Q&A source. The assistant uses it for similar questions from now on.",
    question: "Question",
    questionPlaceholder: "What did the visitor ask?",
    answer: "Answer",
    audiences: "Who can see this answer",
    save: "Save as Q&A source",
    saved: "Staff reply saved as a Q&A source · AI will use it from now on",
    questionRequired: "Add the question this answers",
    answerRequired: "Add the answer",
    audienceRequired: "Pick at least one audience",
    nothing: "There is no reply in this conversation to save yet.",
  },
  shortcuts: {
    title: "Keyboard shortcuts",
    next: "Next conversation",
    previous: "Previous conversation",
    reply: "Reply",
    resolve: "Resolve",
    resolved: "Conversation resolved",
  },
};
